import type { HeatmapData } from "@/lib/conditions/season-heatmap";

export const WEATHER_METRICS = {
  mean_temp_c: { label: "Air temperature", unit: "°C", sports: "both" },
  total_snowfall_cm: {
    label: "Daily snowfall",
    unit: "cm/day",
    sports: "piste",
  },
  snow_depth_m: { label: "Snow depth", unit: "m", sports: "piste" },
  max_swell_m: { label: "Daily maximum swell", unit: "m", sports: "surf" },
  mean_swell_period_s: { label: "Swell period", unit: "s", sports: "surf" },
  mean_wind_kmh: { label: "Wind speed", unit: "km/h", sports: "both" },
} as const;
export type WeatherMetric = keyof typeof WEATHER_METRICS;
export type WeatherRow = { date: string } & Record<
  WeatherMetric,
  number | null
>;
export type WeatherMonth = {
  month: string;
  days: number;
  samples: Record<WeatherMetric, number>;
  values: Record<WeatherMetric, number | null>;
};
export type ClimateHistory = {
  slug: string;
  heatmap: HeatmapData;
  weather: WeatherMonth[];
  water: { month: number; temperature: number | null; sampleYears: number }[];
  window: { start: string; end: string };
  elevation: number | null;
};

/** Complete calendar months only; sparse samples never masquerade as monthly averages. */
export function aggregateWeather(
  rows: readonly WeatherRow[],
  start: string,
  end: string,
): WeatherMonth[] {
  const grouped = new Map<string, Map<string, WeatherRow>>();
  for (const row of rows) {
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(row.date) ||
      row.date < start ||
      row.date > end
    )
      continue;
    const month = row.date.slice(0, 7);
    const days = grouped.get(month) ?? new Map<string, WeatherRow>();
    days.set(row.date, row);
    grouped.set(month, days);
  }
  const months: WeatherMonth[] = [];
  const cursor = new Date(`${start.slice(0, 7)}-01T12:00:00Z`);
  while (cursor.toISOString().slice(0, 10) <= end) {
    const month = cursor.toISOString().slice(0, 7);
    const count = new Date(
      Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0),
    ).getUTCDate();
    const fullMonth = `${month}-01` >= start && `${month}-${count}` <= end;
    const samples = {} as Record<WeatherMetric, number>;
    const values = {} as Record<WeatherMetric, number | null>;
    for (const metric of Object.keys(WEATHER_METRICS) as WeatherMetric[]) {
      const present = [...(grouped.get(month)?.values() ?? [])].flatMap(
        (row) => {
          const value = row[metric];
          return typeof value === "number" && Number.isFinite(value)
            ? [value]
            : [];
        },
      );
      samples[metric] = present.length;
      values[metric] =
        fullMonth && present.length >= Math.ceil(count * 0.8)
          ? Math.round(
              (present.reduce((sum, n) => sum + n, 0) / present.length) * 100,
            ) / 100
          : null;
    }
    months.push({ month, days: count, samples, values });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return months;
}
